import { find, map } from 'lodash';
import { ChangeEvent } from 'react';
import moment from 'moment-timezone';

import * as DocumentService from 'common/services/DocumentService';
import {
    ClientSetting,
    Document,
    InstructionsSetting,
    Order,
    DocumentContact,
    DocumentMilestone,
    OrderMilestone,
} from 'common/types';
import {
    CLIENT_SETTINGS,
    ClientSettingAPI,
    DOCUMENT_TYPE,
    DocumentTradeTypeAPI,
    OrderContactTypeAPI,
    QUOTE_TYPE,
    WorkOrderContactTypeAPI,
} from 'common/stores/redux/MetaStore/MetaStoreApi.types';

export interface Contact extends DocumentContact {
    _tempKey?: string;
}

export const isValidDocumentContact = (
    contact: Contact,
    props?: {
        tradeSubTypeId: string;
        occupied: boolean;
        documentSubTypeId: string;
    }
) => {
    // Quote/WO basically required email or mobile or phone
    // but for trade type commercial and properties occupied, it will required email or mobile
    const { mobile, email, primaryContact } = contact;
    const validContact =
        DocumentService.isValidContact(contact) &&
        ((props?.tradeSubTypeId === 'COMMERCIAL' ||
            props?.documentSubTypeId === 'INSPECTION' ||
            props?.occupied) &&
        primaryContact
            ? email || mobile
            : true);
    return validContact;
};

interface SaveButtonState extends Contact {
    disabled: boolean;
}
export const saveContactButtonDisabled = (
    state: SaveButtonState,
    props: {
        tradeSubTypeId: string;
        occupied: boolean;
        documentSubTypeId: string;
    },
    canChangePrimary?: boolean
) => {
    if (state.disabled || canChangePrimary === false) {
        return true;
    }
    if (!isValidDocumentContact(state, props)) {
        return true;
    }
    return false;
};

export const removePreviousPrimaryContact = (
    id: number,
    originalContacts: Contact[],
    newContacts: Contact[]
) => {
    const primaryContact = originalContacts.find(c => c.primaryContact);
    if (primaryContact && primaryContact.id !== id) {
        const contacts = newContacts.filter(c => c.id !== primaryContact.id);
        return [...contacts, { ...primaryContact, primaryContact: false }];
    }
    return newContacts;
};

export function getContactTypes(
    documentTypeId: string,
    workOrderContactTypes: WorkOrderContactTypeAPI[],
    orderContactTypes: OrderContactTypeAPI[]
) {
    if (
        documentTypeId === DOCUMENT_TYPE.WORK_ORDER ||
        documentTypeId === DOCUMENT_TYPE.QUOTE
    ) {
        return workOrderContactTypes;
    }
    return orderContactTypes;
}

export function getSpecialInstructionsForWorkOrder(
    workOrderTypeId: string,
    tradeTypeId: string,
    tradeSubTypeId: string,
    settings: ClientSetting[]
): string {
    const specialInstructionsSetting = find(settings, {
        settingTypeId: CLIENT_SETTINGS.INSTRUCTIONS,
        setting: {
            documentSubTypeId: workOrderTypeId,
            tradeTypeId,
            tradeSubTypeId,
        },
    }) as InstructionsSetting;
    return specialInstructionsSetting?.setting?.instructions || '';
}

export async function getDocumentSignedUrl(
    evt: ChangeEvent<HTMLButtonElement>,
    key = ''
) {
    evt.preventDefault();
    let documentUrl = key;
    const isCloudinaryLink = documentUrl?.toLowerCase()?.includes('cloudinary');
    if (!isCloudinaryLink) {
        documentUrl = await DocumentService.getReportSignedUrl(key);
    }
    window.open(documentUrl, '_blank');
}

/**
 * combines trade and sub types into single array
 * @param {Object} tradeTypes
 * @returns {Array}
 */

export function buildTradeAndSubTypes(
    tradeTypes: DocumentTradeTypeAPI[] | undefined
) {
    return tradeTypes?.reduce<
        (DocumentTradeTypeAPI<string> & {
            tradeTypeId: string;
            tradeSubTypeId: string;
        })[]
    >((acc, next) => {
        const tradeAndSubTypesForTradeType = map(
            next.tradeSubTypes,
            subType => ({
                id: `${next.id}-${subType.id}`,
                display: `${next.display} - ${subType.display}`,
                tradeTypeId: next.id,
                tradeSubTypeId: subType.id,
            })
        );
        return acc.concat(tradeAndSubTypesForTradeType);
    }, []);
}

export function getLineOfBusiness(
    settingTypes: ClientSettingAPI[] | undefined
) {
    const lineOfBusiness = find(settingTypes, s => s.id === 'LINE_OF_BUSINESS');
    const lineOfBusinessSettings = lineOfBusiness?.settings;
    if (!lineOfBusinessSettings || !lineOfBusinessSettings.types) {
        return [];
    }
    if (Array.isArray(lineOfBusinessSettings.types)) {
        return lineOfBusinessSettings.types;
    }
    return Object.values(lineOfBusinessSettings.types);
}

export function filterManualNotes(
    note: {
        documentId: number;
        isManual: boolean;
        created: string;
    },
    statusNote: {
        id: number;
        status_start: string;
        status_end?: string;
    }
) {
    if (!note.isManual || note.documentId !== statusNote.id) {
        return false;
    }

    const noteDate = moment(note.created);
    const statusStart = moment(statusNote.status_start);

    if (statusNote.status_end) {
        const statusEnd = moment(statusNote.status_end);
        return noteDate.isBetween(statusStart, statusEnd, null, '()');
    }

    return noteDate.isAfter(statusStart);
}

export function sortStatusNotes(
    document: Document,
    notes: {
        documentId: number;
        isManual: boolean;
        created: string;
    }[],
    documentMilestones: DocumentMilestone[] = [],
    orderMilestones: OrderMilestone[] = []
): (DocumentMilestone | OrderMilestone)[] {
    const isOrder = document.documentTypeId === DOCUMENT_TYPE.ORDER;
    const milestones = isOrder ? orderMilestones : documentMilestones;

    const filteredMilestones = milestones.filter(item => {
        // Check if the milestone should be shown based on its status duration or manual notes
        const hasManualNotes = notes.some(note =>
            filterManualNotes(note, {
                id: item.id,
                status_start: item.status_start,
                status_end: item.status_end,
            })
        );
        const shouldShowMilestone =
            !item.status_end ||
            moment(item.status_end).diff(moment(item.status_start), 'minutes') >
                10 ||
            hasManualNotes;

        if (isOrder) {
            const { order_type_id } = item as OrderMilestone;
            const orderDoc = document as Order;
            return (
                shouldShowMilestone &&
                item.job_id === document.jobId &&
                order_type_id === orderDoc.orderTypeId
            );
        }
        const { id, source } = item as DocumentMilestone;
        return (
            shouldShowMilestone &&
            item.job_id === document.jobId &&
            id === document.id &&
            source.toUpperCase() === document.documentTypeId
        );
    });

    // Combine adjacent milestones with the same status_id and status_milestone
    const combinedMilestones: (DocumentMilestone | OrderMilestone)[] = [];
    let currentGroup: (DocumentMilestone | OrderMilestone)[] = [];

    filteredMilestones.forEach((milestone, index) => {
        if (index === 0) {
            currentGroup.push(milestone);
        } else {
            const prevMilestone = filteredMilestones[index - 1];
            if (
                milestone.status_id === prevMilestone.status_id &&
                milestone.status_milestone === prevMilestone.status_milestone
            ) {
                currentGroup.push(milestone);
            } else {
                combinedMilestones.push(combineGroup(currentGroup));
                currentGroup = [milestone];
            }
        }
    });

    if (currentGroup.length > 0) {
        combinedMilestones.push(combineGroup(currentGroup));
    }

    return [...combinedMilestones]?.sort((a, b) => {
        const aDate = moment(a.status_start).tz(a.timezone);
        const bDate = moment(b.status_start).tz(b.timezone);
        return aDate?.diff(bDate);
    });
}

export function documentHistoryHasEquipmentWarrantyAssets(
    document: { id: number; documentTypeId: string },
    orderMilestones: OrderMilestone[]
) {
    return (
        document.documentTypeId === DOCUMENT_TYPE.ORDER &&
        orderMilestones.some(
            milestone =>
                milestone.id === document.id &&
                milestone.has_equipment_warranty_assets
        )
    );
}

function combineGroup(
    group: (DocumentMilestone | OrderMilestone)[]
): DocumentMilestone | OrderMilestone {
    if (group.length === 1) {
        return group[0];
    }

    const sortedGroup = [...group].sort(
        (a, b) => parseInt(a.revision, 10) - parseInt(b.revision, 10)
    );
    const earliestMilestone = sortedGroup[0];
    const latestMilestone = sortedGroup[sortedGroup.length - 1];

    return {
        ...earliestMilestone,
        status_start: earliestMilestone.status_start,
        status_end: latestMilestone.status_end,
        revision: earliestMilestone.revision,
    };
}

const FILE_CATEGORY_DISPLAY_LABELS: Record<string, string> = {
    'Proof of Insurance': 'Certificate of Insurance',
};

export function formatFileCategoryLabel(category: string) {
    return FILE_CATEGORY_DISPLAY_LABELS[category] ?? category;
}

export function isOrderDocument(documentTypeId: string) {
    return documentTypeId === DOCUMENT_TYPE.ORDER;
}

export function isQuoteDocument(documentTypeId: string) {
    return documentTypeId === DOCUMENT_TYPE.QUOTE;
}

export function isWorkOrderDocument(documentTypeId: string) {
    return documentTypeId === DOCUMENT_TYPE.WORK_ORDER;
}

export function isDraftDocument(documentTypeId: string) {
    return documentTypeId === DOCUMENT_TYPE.DRAFT;
}

export function isEquipmentOnlyQuoteDocument(
    documentTypeId: string,
    documentSubTypeId: string
) {
    return (
        documentTypeId === DOCUMENT_TYPE.QUOTE &&
        documentSubTypeId === QUOTE_TYPE.EQUIPMENT_ONLY
    );
}
